// Capacitor native integrations for Android
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';
import { Network } from '@capacitor/network';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Toast } from '@capacitor/toast';
import { Preferences } from '@capacitor/preferences';

// Check if running on native platform
export const isNative = Capacitor.isNativePlatform();
export const isAndroid = Capacitor.getPlatform() === 'android';

// Initialize native features
export const initNativeFeatures = async () => {
  if (!isNative) return;

  try {
    // Configure status bar for dark theme
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#1A1F2E' });

    // Hide splash screen after app is ready
    await SplashScreen.hide();

    // Setup keyboard listeners
    Keyboard.addListener('keyboardWillShow', (info) => {
      document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
    });

    Keyboard.addListener('keyboardWillHide', () => {
      document.body.style.setProperty('--keyboard-height', '0px');
    });

    // Handle app state changes (background/foreground)
    App.addListener('appStateChange', ({ isActive }) => {
      console.log('App state changed. Is active?', isActive);
    });

    // Handle back button on Android
    App.addListener('backButton', ({ canGoBack }) => {
      if (!canGoBack) {
        App.exitApp();
      } else {
        window.history.back();
      }
    });

  } catch (error) {
    console.error('Failed to initialize native features:', error);
  }
};

// Network status helpers
export const getNetworkStatus = async () => {
  const status = await Network.getStatus();
  return status.connected;
};

export const addNetworkListener = (callback) => {
  return Network.addListener('networkStatusChange', (status) => {
    callback(status.connected);
  });
};

// Haptic feedback helpers
export const hapticImpact = async (style = 'medium') => {
  if (!isNative) return;
  
  const styleMap = {
    light: ImpactStyle.Light,
    medium: ImpactStyle.Medium,
    heavy: ImpactStyle.Heavy
  };
  
  await Haptics.impact({ style: styleMap[style] || ImpactStyle.Medium });
};

export const hapticNotification = async (type = 'success') => {
  if (!isNative) return;
  
  const typeMap = {
    success: NotificationType.Success,
    warning: NotificationType.Warning,
    error: NotificationType.Error
  };
  
  await Haptics.notification({ type: typeMap[type] || NotificationType.Success });
};

export const hapticVibrate = async () => {
  if (!isNative) return;
  await Haptics.vibrate();
};

// Toast helpers (native toasts)
export const showNativeToast = async (text, duration = 'short', position = 'bottom') => {
  if (!isNative) return;
  
  await Toast.show({
    text,
    duration: duration === 'long' ? 'long' : 'short',
    position
  });
};

// Preferences (native storage) - more reliable than localStorage
export const setPreference = async (key, value) => {
  await Preferences.set({
    key,
    value: JSON.stringify(value)
  });
};

export const getPreference = async (key) => {
  const { value } = await Preferences.get({ key });
  return value ? JSON.parse(value) : null;
};

export const removePreference = async (key) => {
  await Preferences.remove({ key });
};

export const clearPreferences = async () => {
  await Preferences.clear();
};

// Update status bar based on theme
export const updateStatusBarForTheme = async (themeId) => {
  if (!isNative) return;
  
  const darkThemes = ['default', 'fantasy', 'cyberpunk', 'steampunk', 'ocean', 'aurora', 'sunset'];
  const isLightTheme = themeId === 'light';
  
  try {
    await StatusBar.setStyle({ 
      style: isLightTheme ? Style.Light : Style.Dark 
    });
    
    // Set background color based on theme
    const bgColors = {
      default: '#1A1F2E',
      fantasy: '#1C1410',
      cyberpunk: '#0A0515',
      steampunk: '#1A1611',
      ocean: '#0A1628',
      light: '#FAFAFA',
      aurora: '#0B1426',
      sunset: '#1A1114'
    };
    
    await StatusBar.setBackgroundColor({ 
      color: bgColors[themeId] || '#1A1F2E' 
    });
  } catch (error) {
    console.error('Failed to update status bar:', error);
  }
};
